// Source: https://github.com/vinted/dwh-schema-registry/blob/5387d2ce7b44ee93711a15dfb97c60a4d984f22c/avro/events/common/shared/user.avdl#L25
type UserTarget =
  | 'close_screen'
  | 'rearrange_media'
  | 'upload_price_suggestions_info_modal_open'
  | 'submit'
  | 'learn_more'
  | 'give_feedback'
  | 'physical_auth_how_it_works'
  | 'electronics_verification_how_it_works'
  | 'producer_responsibility_info'

// https://github.com/vinted/dwh-schema-registry/blob/master/avro/events/common/shared/common.avdl#L34
type ContentSource = 'edit_price'

type ItemUploadStartEventArgs = {
  screenName: string
  tempUuid?: string
}

type ItemUploadStartEventExtra = {
  upload_session_id?: string
  screen: string
}

export const itemUploadStartEventExtra = (args: ItemUploadStartEventArgs) => {
  const extra: ItemUploadStartEventExtra = {
    screen: args.screenName,
    upload_session_id: args.tempUuid,
  }

  return {
    event: 'user.upload_item.start',
    extra,
  }
}

type ItemUploadSubmitSuccessArgs = {
  edited?: boolean
  photoCount?: number
  videoCount?: number
  itemId?: number | string
  screen?: string
  tempUuid?: string
}

type ItemUploadSubmitSuccessExtra = {
  edited?: boolean
  photo_count?: number
  video_count?: number
  item_id?: number | string
  screen?: string
  upload_session_id?: string
}

export const itemUploadSubmitSuccess = (args: ItemUploadSubmitSuccessArgs) => {
  const extra: ItemUploadSubmitSuccessExtra = {
    edited: args.edited,
    photo_count: args.photoCount,
    video_count: args.videoCount,
    item_id: args.itemId,
    screen: args.screen,
    upload_session_id: args.tempUuid,
  }

  return {
    event: 'user.upload_item.submit_success',
    extra,
  }
}

type ItemUploadViewSimilarSoldItemEventArgs = {
  id: number | string
  screen?: string
  position: number
  contentSource: ContentSource
  uploadSessionId?: string
}

type ItemUploadViewSimilarSoldItemEventExtra = {
  id: number | string
  screen?: string
  position: number
  content_source: ContentSource
  upload_session_id: string | undefined
}

export const itemUploadViewSimilarSoldItemEvent = (
  args: ItemUploadViewSimilarSoldItemEventArgs,
) => {
  const { id, screen, position, contentSource, uploadSessionId } = args

  const extra: ItemUploadViewSimilarSoldItemEventExtra = {
    id,
    position,
    content_source: contentSource,
    upload_session_id: uploadSessionId,
  }

  if (screen) extra.screen = screen

  return { event: 'upload_form.user.view_similar_sold_item', extra }
}

type ClickEventArgs = {
  target: UserTarget
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: UserTarget
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: ClickEventArgs) => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

// Source: https://github.com/vinted/dwh-schema-registry/blob/master/avro/events/user/view.avdl#L25
type ViewEventTarget =
  | 'physical_auth_eligible'
  | 'value_select_modal'
  | 'bundle_discount_settings'
  | 'electronics_verification_eligible'

type ViewEventArgs = {
  target: ViewEventTarget
  targetDetails?: string
  screen?: string
}

type ViewEventExtra = {
  target: ViewEventTarget
  target_details?: string
  screen?: string
}

export const viewEvent = (args: ViewEventArgs) => {
  const { target, targetDetails, screen } = args

  const extra: ViewEventExtra = {
    target,
  }

  if (targetDetails) extra.target_details = targetDetails
  if (screen) extra.screen = screen

  return {
    event: 'user.view',
    extra,
  }
}

type UserInputEventArgs = {
  screen?: string
  target: string
  value?: string | null
  state: 'focus' | 'unfocus'
  tempUuid?: string
  isDynamic?: boolean
}

export const userInputEvent = (args: UserInputEventArgs) => {
  const { state, target, value, screen, tempUuid, isDynamic } = args
  const extra = {
    screen,
    target,
    value,
    state,
    upload_session_id: tempUuid,
    is_dynamic: isDynamic || false,
  }

  return {
    event: 'user.input',
    extra,
  }
}

type ItemUploadClickEventArgs = {
  screen?: string
  target: UserTarget
  targetDetails?: string | null
  uploadSessionId: string | null
}

type ItemUploadClickEventExtra = {
  screen?: string
  target: UserTarget
  target_details?: string | null
  upload_session_id: string | null
}

export const itemUploadClickEvent = (args: ItemUploadClickEventArgs) => {
  const { screen, target, targetDetails, uploadSessionId } = args

  const extra: ItemUploadClickEventExtra = {
    target,
    upload_session_id: uploadSessionId,
  }

  if (screen) extra.screen = screen
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'upload_form.user.click',
    extra,
  }
}

type ItemUploadViewScreenEventArgs = {
  screen: string
  details?: string
  uploadSessionId: string | null
}

type ItemUploadViewScreenEventExtra = {
  screen: string
  details?: string
  upload_session_id: string | null
}

export const itemUploadViewScreenEvent = (args: ItemUploadViewScreenEventArgs) => {
  const { screen, details, uploadSessionId } = args

  const extra: ItemUploadViewScreenEventExtra = {
    screen,
    upload_session_id: uploadSessionId,
  }

  if (details) extra.details = details

  return {
    event: 'upload_form.user.view_screen',
    extra,
  }
}
